import React, { useState } from "react";
import useGetHealthLogs from "../../hooks/useGetHealthLogs.js";
import { RefreshCw, Plus } from "lucide-react";

import DetailsTable from "../detailsView/DetailsTable.js";
import LoadingSpinner from "../components/LoadingSpinner.js";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Select } from "components/ui/select";
import { Card, CardContent, CardTitle } from "components/ui/card";

function ErrorAlert({ error }) {
    return (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            <strong>Warning!</strong> {error}
        </div>
    );
}

function TimeOutAlert({ refetchQueryResults }) {
    return (
        <div className="flex items-center gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning">
            <span><strong>Time Out!</strong> Please refresh after some time</span>
            <Button size="icon-sm" onClick={refetchQueryResults} title="Refresh">
                <RefreshCw />
            </Button>
        </div>
    );
}

function KeyBadges({ extraQueryArg, removeExtraQueryArg }) {
    return (
        <div className="flex flex-wrap gap-2">
            {Object.keys(extraQueryArg).map((key) =>
                extraQueryArg[key] != "" ? (
                    <button
                        key={key}
                        onClick={() => removeExtraQueryArg(key)}
                        title="Remove filter"
                        className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                    >
                        <span>{key}: {extraQueryArg[key]}</span>
                        <span aria-hidden="true">&times;</span>
                    </button>
                ) : (
                    ""
                )
            )}
        </div>
    );
}

let search_key_list = [
    "statusCode",
    "remoteAddr",
    "userAgent",
    "upstreamAddr",
    "forwaredFor",
    "cdn",
];
let emptyDict = {};
search_key_list.forEach((key) => {
    emptyDict[key] = "";
});

function StatusCodeView({ eventId, environment, startTime, endTime, theme }) {
    const [queryId, setQueryId] = useState(null);
    const [extraQueryArg, setExtraQueryArg] = useState(emptyDict);
    const [isRequestStrted, setIsRequestStrted] = useState(false);
    const [extraQueryArgFlag, setExtraQueryArgFlag] = useState(0);
    const [selectedKey, setSelectedKey] = useState(search_key_list[0]);
    const [selectedValue, setSelectedValue] = useState("");

    function updateExtraQueryArg() {
        if (selectedKey && selectedValue) {
            extraQueryArg[selectedKey] = selectedValue.trim();
            setSelectedKey(search_key_list[0]);
            setSelectedValue("");
            setExtraQueryArgFlag(Math.random());
        }
    }

    function removeExtraQueryArg(key) {
        if (key) {
            extraQueryArg[key] = "";
            setSelectedKey(search_key_list[0]);
            setSelectedValue("");
            setExtraQueryArgFlag(Math.random());
        }
    }

    const { queryLogs, error, isLoaded, isTimeOut, refetchQueryResults } = useGetHealthLogs(
        "filter_url_by_status_code",
        eventId,
        environment,
        startTime,
        endTime,
        setQueryId,
        extraQueryArg,
        false
    );

    function getRequestsUrls() {
        refetchQueryResults();
        setIsRequestStrted(true);
    }

    let cardBodyContent;
    if (isRequestStrted && queryLogs.length !== 0) {
        cardBodyContent = (
            <div className="mt-2 grid grid-cols-1 gap-4 lg:grid-cols-3">
                {getLogFieldCount("remoteAddr")}
                {getLogFieldCount("forwaredFor")}
                {getLogFieldCount("upstreamAddr")}
                {getLogFieldCount("userAgent")}
                {getLogFieldCount("cdn")}
            </div>
        );
    } else if (error) {
        cardBodyContent = <ErrorAlert error={error} />;
    } else if (isTimeOut) {
        cardBodyContent = <TimeOutAlert refetchQueryResults={refetchQueryResults} />;
    } else if (!isRequestStrted) {
        cardBodyContent = "";
    } else {
        cardBodyContent = "";
    }

    return (
        <div className="pb-3">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className="space-y-3">
                    <div className="flex items-end gap-2">
                        <Select
                            id="keyList"
                            value={selectedKey}
                            onChange={(e) => setSelectedKey(e.target.value)}
                            className="w-auto"
                        >
                            {search_key_list.map((item, index) => (
                                <option value={item} key={index}>
                                    {item}
                                </option>
                            ))}
                        </Select>
                        <Input
                            type="text"
                            id="value"
                            name="value"
                            value={selectedValue}
                            onChange={(e) => setSelectedValue(e.target.value)}
                            placeholder="Value"
                        />
                        <Button size="icon" onClick={updateExtraQueryArg} title="Add filter">
                            <Plus />
                        </Button>
                    </div>
                    <div>
                        <Button onClick={getRequestsUrls}>Filter urls</Button>
                    </div>
                </div>
                <div>
                    <KeyBadges
                        extraQueryArg={extraQueryArg}
                        removeExtraQueryArg={removeExtraQueryArg}
                    />
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                    {!isLoaded && isRequestStrted ? <LoadingSpinner /> : recordsCountLabel()}
                </div>
            </div>

            {cardBodyContent}
        </div>
    );

    function getLogFieldCount(key) {
        const uniqueValueCount = {};

        for (let item of queryLogs.log_data) {
            if (uniqueValueCount[item[key]]) {
                uniqueValueCount[item[key]]++;
            } else {
                uniqueValueCount[item[key]] = 1;
            }
        }

        let keyCountList = Object.keys(uniqueValueCount).map((item) => ({
            [key]: item,
            count: uniqueValueCount[item],
        }));

        keyCountList.sort((a, b) => b.count - a.count);

        return (
            <Card>
                <CardContent className="p-4">
                    <CardTitle className="mb-2 text-base">
                        {key.charAt(0).toUpperCase() +
                            key
                                .slice(1)
                                .replace(/([A-Z])/g, " $1")
                                .trim()}
                    </CardTitle>
                    <DetailsTable
                        logData={keyCountList}
                        keyList={[key, "count"]}
                        theme={theme}
                        itemsPerPage={20}
                    />
                </CardContent>
            </Card>
        );
    }

    function recordsCountLabel() {
        if (queryLogs.length !== 0) {
            return (
                <div className="text-sm">
                    <span className="mr-3">
                        Records matched: <strong>{queryLogs.statistics.recordsMatched}</strong>
                    </span>
                    <span>
                        Showing results: <strong>{queryLogs.log_data.length}</strong>
                    </span>
                </div>
            );
        }
    }
}

export default StatusCodeView;
